import React, { useState, useMemo, useEffect, useCallback } from "react";
import styled from "styled-components/macro";
import { rgba } from "polished";
import {
  NavLink,
  withRouter,
  RouteComponentProps,
  useLocation,
} from "react-router-dom";
import { RouteType, RouteChildType } from "../types/routes";
import PerfectScrollbar from "react-perfect-scrollbar";
import "../vendor/perfect-scrollbar.css";

import { spacing } from "@material-ui/system";

import {
  Box as MuiBox,
  Chip,
  Collapse,
  Drawer as MuiDrawer,
  List as MuiList,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import { Field, Typography, Select } from "components";
import { ExpandLess, ExpandMore } from "@material-ui/icons";

import { MASTER_MENU, TENANTED_MENU } from "routes/menu";
import { getTenantIdFromUrl } from "routes/paths";
import { homePath, getTenantPath } from "utils/helper";

import { nodeEnv } from "env";
import {
  compose,
  withFormik,
  withHooks,
  withStores,
  withTranslation,
} from "enhancers";
import { gql } from "@apollo/client";

import PopMenu from "components/common/PopMenu";
import LanguagesPopMenu from "./common/LanguagesPopMenu";
import { AppColor } from "theme/app-color";

const Box = styled(MuiBox)(spacing);

const Drawer = styled(MuiDrawer)`
  border-right: 0;

  > div {
    border-right: 0;
  }
`;

const Scrollbar = styled(PerfectScrollbar)`
  background-color: ${AppColor["Text/Secondary"]};
  border-right: 1px solid rgba(0, 0, 0, 0.12);
`;

const List = styled(MuiList)`
  background-color: ${AppColor["Text/Secondary"]};
`;

const Items = styled.div`
  padding-top: ${(props) => props.theme.spacing(2.5)}px;
  padding-bottom: ${(props) => props.theme.spacing(2.5)}px;
`;

const WorkspaceDropdown = styled(Field)`
  .MuiSelect-root {
    background-color: white;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Brand = styled(ListItem)<{
  button?: boolean;
  component?: React.ReactNode;
  to?: string;
}>`
  font-size: ${(props) => props.theme.typography.h5.fontSize};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  color: ${(props) => props.theme.sidebar.header.color};
  background-color: ${AppColor["Text/Secondary"]};
  font-family: ${(props) => props.theme.typography.fontFamily};
  min-height: 56px;
  padding-left: ${(props) => props.theme.spacing(0)}px;
  padding-right: ${(props) => props.theme.spacing(0)}px;
  justify-content: flex-start;
  cursor: pointer;

  ${(props) => props.theme.breakpoints.up("sm")} {
    min-height: 64px;
  }

  &:hover {
    background-color: ${AppColor["Text/Secondary"]};
  }
`;

const BrandChip = styled(Chip)`
  border-radius: 5px;
  color: ${(props) => props.theme.palette.common.white};
  font-size: 55%;
  height: 18px;
  margin-left: 2px;
  margin-top: -16px;
  padding: 3px 0;

  span {
    padding-left: ${(props) => props.theme.spacing(1.375)}px;
    padding-right: ${(props) => props.theme.spacing(1.375)}px;
  }
`;

type CategoryType = {
  activeClassName?: string;
  button?: boolean;
  onClick?: () => void;
  to?: string;
  component?: typeof NavLink;
  exact?: boolean;
};

const Category = styled(ListItem)<CategoryType>`
  padding-top: ${(props) => props.theme.spacing(3)}px;
  padding-bottom: ${(props) => props.theme.spacing(3)}px;
  padding-left: ${(props) => props.theme.spacing(5)}px;
  padding-right: ${(props) => props.theme.spacing(5)}px;
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};

  svg {
    color: ${(props) => props.theme.sidebar.color};
    font-size: 20px;
    width: 24px;
    height: 24px;
    opacity: 1;
  }

  &:hover {
    background: ${AppColor["Primary/Primary"]};
  }

  &.${(props) => props.activeClassName} {
    background-color: ${AppColor["Primary/Primary"]};

    span {
      color: ${AppColor["Text/White"]};
    }
  }
`;

const CategoryText = styled(ListItemText)`
  margin: 0;
  span {
    color: ${(props) => props.theme.sidebar.color};
    font-size: ${(props) => props.theme.typography.body1.fontSize}px;
    padding: 0 ${(props) => props.theme.spacing(4)}px;
  }
`;

const CategoryIconLess = styled(ExpandLess)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const CategoryIconMore = styled(ExpandMore)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const Link = styled(ListItem)<{
  activeClassName: string;
  component: typeof NavLink;
  exact: boolean;
  to: string;
}>`
  padding-top: ${(props) => props.theme.spacing(3)}px;
  padding-bottom: ${(props) => props.theme.spacing(3)}px;
  padding-left: ${(props) => props.theme.spacing(5)}px;
  padding-right: ${(props) => props.theme.spacing(5)}px;
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};

  svg {
    color: ${(props) => props.theme.sidebar.color};
    font-size: 20px;
    width: 24px;
    height: 24px;
    opacity: 1;
  }

  span {
    color: ${(props) => props.theme.sidebar.color};
  }

  &:hover {
    background: ${AppColor["Primary/Primary"]};
  }

  &.${(props) => props.activeClassName} {
    background-color: ${AppColor["Primary/Primary"]};

    span {
      color: ${AppColor["Text/White"]};
    }
  }
`;

const LinkBadge = styled(Chip)`
  font-size: 11px;
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  height: 20px;
  position: absolute;
  right: 28px;
  top: 8px;
  background: ${(props) => props.theme.sidebar.badge.background};

  span.MuiChip-label,
  span.MuiChip-label:hover {
    cursor: pointer;
    color: ${(props) => props.theme.sidebar.badge.color};
    padding-left: ${(props) => props.theme.spacing(2)}px;
    padding-right: ${(props) => props.theme.spacing(2)}px;
  }
`;

const CategoryBadge = styled(LinkBadge)`
  top: 12px;
`;

const LinkIconPlaceholder = styled.span`
  width: 24px;
  height: 24px;
  display: inline-block;
  flex-shrink: 0;
`;

const SidebarSection = styled(Typography)`
  color: ${AppColor["Text/White"]};
  padding: ${(props) => props.theme.spacing(4)}px
    ${(props) => props.theme.spacing(7)}px
    ${(props) => props.theme.spacing(1)}px;
  opacity: 1;
  display: block;
`;

const SidebarFooter = styled.div`
  background-color: ${AppColor["Text/Secondary"]};
  padding: 12px 16px;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
`;

const SidebarFooterText = styled(Typography)`
  color: ${AppColor["Text/White"]};
  text-overflow: ellipsis;
  overflow: hidden;
`;

const SidebarFooterSubText = styled(Typography)`
  color: ${AppColor["Text/White"]};
  font-size: 12px;
  line-height: 16px;
  display: block;
  padding: 1px;
`;

const SidebarTopBox = styled(Box)`
  display: flex;
  align-items: center;
  flex-direction: column;
  background-color: ${AppColor["Text/Secondary"]};
`;

// const SidebarFooterBadge = styled(Badge)`
//   margin-right: ${(props) => props.theme.spacing(1)}px;
//   span {
//     background-color: ${(props) =>
//       props.theme.sidebar.footer.online.background};
//     border: 1.5px solid ${(props) => props.theme.palette.common.white};
//     height: 12px;
//     width: 12px;
//     border-radius: 50%;
//   }
// `;

type SidebarCategoryPropsType = {
  name: string;
  icon: JSX.Element;
  classes?: string;
  isOpen?: boolean;
  isCollapsable: boolean;
  badge?: string | number;
  activeClassName?: string;
  button: true;
  onClick?: () => void;
  to?: string;
  component?: typeof NavLink;
  exact?: boolean;
};

const SidebarCategory: React.FC<SidebarCategoryPropsType> = ({
  name,
  icon,
  classes,
  isOpen,
  isCollapsable,
  badge,
  ...rest
}) => {
  const location = useLocation();
  const to = rest.to;
  let activeProps = {};
  if (to) {
    const isActive =
      location.pathname === to || location.pathname.startsWith(to + "/");
    activeProps = { isActive: () => isActive };
  }

  return (
    <Category {...activeProps} {...rest}>
      {icon}
      <CategoryText>{name}</CategoryText>
      {isCollapsable ? (
        isOpen ? (
          <CategoryIconMore />
        ) : (
          <CategoryIconLess />
        )
      ) : null}
      {badge ? <CategoryBadge label={badge} /> : ""}
    </Category>
  );
};

type SidebarLinkPropsType = {
  name: string;
  to: string;
  badge?: string | number;
  icon?: JSX.Element;
};

const SidebarLink: React.FC<SidebarLinkPropsType> = ({
  name,
  to,
  badge,
  icon,
}) => {
  const location = useLocation();
  const isActive =
    location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <Link
      button
      // dense
      component={NavLink}
      to={to}
      activeClassName="active"
      isActive={() => isActive}
    >
      {icon ?? <LinkIconPlaceholder />}
      <CategoryText>{name}</CategoryText>
      {badge ? <LinkBadge label={badge} /> : ""}
    </Link>
  );
};

type SidebarPropsType = {
  classes?: string;
  staticContext: string;
  location: {
    pathname: string;
  };
  routes: Array<RouteType>;
  currentUser?: any;
  PaperProps: {
    style: {
      width: number;
    };
  };
  variant?: "permanent" | "persistent" | "temporary";
  open?: boolean;
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
  t: Function;
  onClose?: () => void;
  workspacesOption: Array<{
    id: string;
    name: string;
  }>;
  currentWorkspace?: {
    id: string;
    isMaster: boolean;
  };
};

const ENV_TAGS: any = {
  production: null,
  uat: null,
  staging: (
    <BrandChip
      label="STG"
      style={{ backgroundColor: "#1bc5bd", color: "#1e293a" }}
    />
  ),
  staging2: (
    <BrandChip
      label="STG"
      style={{ backgroundColor: "#1bc5bd", color: "#1e293a" }}
    />
  ),
  staging3: (
    <BrandChip
      label="STG"
      style={{ backgroundColor: "#1bc5bd", color: "#1e293a" }}
    />
  ),
  development: (
    <BrandChip
      label="DEV"
      style={{ backgroundColor: "#ffeb3b", color: "#1e293a" }}
    />
  ),
  development2: (
    <BrandChip
      label="DEV"
      style={{ backgroundColor: "#ffeb3b", color: "#1e293a" }}
    />
  ),
};
const ENV_TAG: any = ENV_TAGS[nodeEnv];

const Sidebar: React.FC<RouteComponentProps & SidebarPropsType> = ({
  classes,
  staticContext,
  location,
  isAdmin,
  isSuperAdmin,
  t,
  currentUser,
  workspacesOption,
  currentWorkspace,
  ...rest
}) => {
  type tplotOptions = {
    [key: number]: boolean;
  };

  const tenantId = currentWorkspace?.id || getTenantIdFromUrl();

  const resolvedSidebarRoutes = useMemo(() => {
    const routes = currentWorkspace?.isMaster ? MASTER_MENU : TENANTED_MENU;
    if (!tenantId) return routes;

    return routes.map((route: RouteType) => {
      const newRoute: RouteType = {
        ...route,
        path: getTenantPath(route.path, tenantId),
      };
      if (route.children) {
        newRoute.children = route.children.map((child: RouteChildType) => ({
          ...child,
          path: getTenantPath(child.path, tenantId),
        }));
      }
      return newRoute;
    });
  }, [currentWorkspace, tenantId]);

  const initOpenRoutes = useCallback((): tplotOptions => {
    /* Open collapse element that matches current url */
    const pathName = location.pathname;

    let _routes = {};

    resolvedSidebarRoutes.forEach((route: RouteType, index) => {
      const isActive =
        pathName === route.path || pathName.startsWith(route.path + "/");
      const isOpen = route.open;
      const isHome = route.containsHome && pathName === "/";

      // If this category contains the active child, it should be open
      let containsActiveChild = false;
      if (route.children) {
        containsActiveChild = route.children.some(
          (child: RouteChildType) =>
            pathName === child.path || pathName.startsWith(child.path + "/")
        );
      }

      _routes = Object.assign({}, _routes, {
        [index]: isActive || isOpen || isHome || containsActiveChild,
      });
    });

    return _routes;
  }, [location.pathname, resolvedSidebarRoutes]);

  const [openRoutes, setOpenRoutes] = useState(() => initOpenRoutes());

  useEffect(() => {
    setOpenRoutes(initOpenRoutes());
  }, [initOpenRoutes]);

  const toggle = (index: number) => {
    // Collapse all elements
    Object.keys(openRoutes).forEach(
      (item) =>
        openRoutes[index] ||
        setOpenRoutes((openRoutes) =>
          Object.assign({}, openRoutes, { [item]: false })
        )
    );

    // Toggle selected element
    setOpenRoutes((openRoutes) =>
      Object.assign({}, openRoutes, { [index]: !openRoutes[index] })
    );
  };

  const userPermissions: string[] = currentUser?.role?.permissions || [];
  const hasAllPermissions = (permissions: any[] = []) =>
    permissions.every((perm) => userPermissions.includes(perm));

  return (
    <Drawer variant="permanent" {...rest}>
      <SidebarTopBox>
        <Box display="flex" flexDirection="row">
          <Brand component={NavLink} to="/" button>
            {/* <BrandIcon />{" "} */}
            <Box color="white" ml={1}>
              <Typography variant="Header/24">
                {t(".appName")} {ENV_TAG}
              </Typography>
            </Box>
          </Brand>
          <LanguagesPopMenu />
        </Box>
        <Box width="210px" bgcolor="white" borderRadius="8px">
          <WorkspaceDropdown
            component={Select}
            name="workspacesOption"
            type="text"
            options={workspacesOption}
            disableClearable
            fullWidth
          />
        </Box>
      </SidebarTopBox>
      <Scrollbar>
        <List disablePadding>
          <Items>
            {resolvedSidebarRoutes.map((category: RouteType, index) => {
              const isCategoryAllowed = hasAllPermissions(category.permissions);
              if (!isCategoryAllowed) {
                return null;
              }

              const visibleChildren = category.children
                ? category.children.filter((child: RouteChildType) =>
                    hasAllPermissions(child.permissions)
                  )
                : null;

              const shouldRenderGroup =
                !!category.children &&
                !!category.icon &&
                !!visibleChildren?.length;
              const shouldRenderSingle = !category.children && !!category.icon;

              if (!shouldRenderGroup && !shouldRenderSingle) {
                return null;
              }

              return (
                <React.Fragment key={index}>
                  {category.header ? (
                    <SidebarSection variant="Header/16">
                      {t(category.header)}
                    </SidebarSection>
                  ) : null}

                  {shouldRenderGroup ? (
                    <React.Fragment>
                      <SidebarCategory
                        isOpen={!openRoutes[index]}
                        isCollapsable={true}
                        name={t(category.id)}
                        icon={category.icon!}
                        button={true}
                        onClick={() => toggle(index)}
                      />

                      <Collapse
                        in={openRoutes[index]}
                        timeout="auto"
                        unmountOnExit
                      >
                        {visibleChildren!.map(
                          (route: RouteChildType, index: number) => (
                            <SidebarLink
                              key={index}
                              name={route.name ?? (route.id ? t(route.id) : "")}
                              to={route.path}
                              icon={route.icon}
                              badge={route.badge}
                            />
                          )
                        )}
                      </Collapse>
                    </React.Fragment>
                  ) : (
                    <SidebarCategory
                      isCollapsable={false}
                      name={t(category.id)}
                      to={category.path}
                      activeClassName="active"
                      component={NavLink}
                      icon={category.icon!}
                      button
                      badge={category.badge}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </Items>
        </List>
      </Scrollbar>
      <SidebarFooter>
        <PopMenu
          SidebarFooterText={SidebarFooterText}
          SidebarFooterSubText={SidebarFooterSubText}
        />
      </SidebarFooter>
    </Drawer>
  );
};

export const SIDEBAR_API = {
  GET_CURRENT_USER: gql`
    query GET_CURRENT_USER {
      getCurrentBackofficeUser {
        ownerType
        admin {
          isSuperAdmin
        }
      }
    }
  `,
  GET_WORKSPACES_OPTION: gql`
    query GET_WORKSPACES_OPTION {
      workspacesOptions {
        label
        value
      }
    }
  `,
  GET_WORKSPACE: gql`
    query GET_WORKSPACE($id: String!) {
      workspace(id: $id) {
        id
        name
        isMaster
      }
    }
  `,
};

const enhancer = compose(
  withStores((stores: any) => ({
    currentUser: stores.appStore.currentUser,
  })),
  withFormik({
    mapPropsToValues: (props: any) => {
      const pathname = window.location.pathname;
      const match = pathname.match(/^\/([^/]+)\//);
      const tenantId = match ? match[1] : null;
      return {
        workspacesOption: tenantId,
      };
    },
  }),
  withTranslation({ prefix: "components.SideBar" }),
  withHooks((props: any, hooks: any) => {
    const { useQuery, useMemo, useEffect, useLazyQuery } = hooks;
    const { values, setValues } = props;
    const currentUser = useQuery(SIDEBAR_API.GET_CURRENT_USER);
    const workspacesOptionQuery = useQuery(SIDEBAR_API.GET_WORKSPACES_OPTION);
    const { loading, data, error } = currentUser || {};
    const {
      loading: workspacesOptionLoading,
      data: workspacesOptionData,
      error: workspacesOptionError,
    } = workspacesOptionQuery || {};

    const tenantId = getTenantIdFromUrl();
    const { data: currentWorkspaceData } = useQuery(SIDEBAR_API.GET_WORKSPACE, {
      variables: { id: tenantId },
      skip: !tenantId,
      fetchPolicy: "cache-first",
    });

    const [
      fetchSelectedWorkspace,
      { data: selectedWorkspaceData, loading: selectedWorkspaceLoading },
    ] = useLazyQuery(SIDEBAR_API.GET_WORKSPACE, {
      fetchPolicy: "network-only",
    });

    const ownerType = useMemo(() => {
      if (loading || error) {
        return null;
      }
      return data.getCurrentBackofficeUser?.ownerType;
    }, [loading, data, error]);

    const workspacesOption = useMemo(() => {
      if (workspacesOptionLoading || workspacesOptionError) {
        return [];
      }
      return workspacesOptionData.workspacesOptions;
    }, [workspacesOptionLoading, workspacesOptionError, workspacesOptionData]);

    useEffect(() => {
      const currentTenantId = window.location.pathname.match(
        /^\/([^/]+)\//
      )?.[1];
      const selectedTenantId = values.workspacesOption;

      if (
        currentTenantId &&
        selectedTenantId &&
        currentTenantId !== selectedTenantId
      ) {
        if (!selectedWorkspaceData && !selectedWorkspaceLoading) {
          fetchSelectedWorkspace({ variables: { id: selectedTenantId } });
          return;
        }

        if (selectedWorkspaceLoading || !selectedWorkspaceData) {
          return;
        }

        const permissions =
          props.currentUser?.role?.permissions ||
          data?.getCurrentBackofficeUser?.role?.permissions ||
          [];
        const isMaster = selectedWorkspaceData?.workspace?.isMaster || false;

        const homePathWithoutTenant = homePath(permissions, isMaster);

        const newPath = `/${selectedTenantId}${homePathWithoutTenant}`;
        window.location.href = newPath;
      }
    }, [
      values.workspacesOption,
      selectedWorkspaceData,
      selectedWorkspaceLoading,
      data,
      fetchSelectedWorkspace,
      props.currentUser,
    ]);

    useEffect(() => {
      const currentTenantId = window.location.pathname.match(
        /^\/([^/]+)\//
      )?.[1];
      if (
        currentTenantId &&
        !values.workspacesOption &&
        workspacesOption.length > 0
      ) {
        setValues({ workspacesOption: currentTenantId });
      } else if (
        !currentTenantId &&
        workspacesOption.length > 0 &&
        !values.workspacesOption &&
        data?.getCurrentBackofficeUser?.accessibleTenants?.length > 0
      ) {
        const defaultTenantId =
          data.getCurrentBackofficeUser.accessibleTenants[0];
        setValues({ workspacesOption: defaultTenantId });
      }
    }, [workspacesOption, values.workspacesOption, setValues, data]);

    const isAdmin = ownerType === "Admin";
    const isSuperAdmin = isAdmin
      ? data.getCurrentBackofficeUser?.admin?.isSuperAdmin
      : null;

    const currentWorkspace = currentWorkspaceData?.workspace;

    const propsParams = isSuperAdmin
      ? { ...props, isAdmin, isSuperAdmin, workspacesOption, currentWorkspace }
      : { ...props, isAdmin, workspacesOption, currentWorkspace };
    return propsParams;
  })
);

export default enhancer(withRouter(Sidebar));
